import { toWorkspacePath } from "@/features/Workspaces/workspaceRoutes";
import { useTranslation } from "react-i18next";
import { Form } from "@/components/ui/form";
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Divider,
} from "@heroui/react";
import { z } from "zod";

import { Diff } from "@/features/FormCard/Diff";

import {
  AutocompleteAsyncField,
  DatePickerField,
  InputField,
  SelectFieldWithSection,
  TextareaField,
  TSections,
} from "@/features/FormCard/Fields";

import {
  dateSchema,
  preprocessTextAsyncAutocompleteField,
} from "@/features/FormCard/utils";

import {
  ConfirmationModal,
  DeleteConfirmationModal,
} from "@/features/FormCard/Modals";

import { parseToDateValue } from "@/features/FormCard/utils";

import { FormActionsUpdate } from "@/features/FormCard/Actions";

import { ExternalLink } from "@/components/ExternalLink";
import { FixingMode } from "@/data/fixingMode";
import { useEditForm } from "@/features/Edit/hooks";
import { useEffect, useMemo, useRef } from "react";
import { fieldTranslations } from "./translations";
import { useCategoryAutoSelection } from "./hook";
import { computeFixingMode } from "../C8TimeSeries/utils";
import i18n from "@/i18n";

const FormSchema = z.object({
  date: z.preprocess((val) => parseToDateValue(val), dateSchema),
  c8_static_datum_name: z.preprocess(
    preprocessTextAsyncAutocompleteField,
    z.union([
      // we recv / send as string the data
      z.string(),

      // for the autocomplete, the form state is an object
      z.object({
        text: z.string(),
        id: z.number(),
        asset_class_2: z.string(),
        asset_class_1: z.string(),
        currency: z.string(),
      }),
    ]),
  ),
  c8_static_datum_asset_class_1: z.string().nullable().optional(),
  c8_static_datum_asset_class_2: z.string().nullable().optional(),
  c8_static_datum_id: z.number(),
  quantity: z.coerce.number().nullable().optional(),
  market_value_ptf_ccy: z.coerce.number().nullable().optional(),
  market_value_local_ccy: z.coerce.number(),
  category: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  fees: z.coerce.number().nullable().optional(),
  tax: z.coerce.number().nullable().optional(),
  created_at: z.string().nullable().optional(),
  updated_at: z.string().nullable().optional(),
  time_serie_file_id: z.number().nullable().optional(),
  c8_static_datum_currency: z.string().nullable().optional(),
});

type BaseForm = z.infer<typeof FormSchema>;

export type TForm = Omit<BaseForm, "c8_static_datum_name"> & {
  c8_static_datum_name: Extract<
    BaseForm["c8_static_datum_name"],
    { id: number }
  >;
};

export function C8TransactionsEditCard({
  id,
  client,
  table_name,
}: {
  id: number;
  client: string;
  table_name: string;
}) {
  const { t } = useTranslation();
  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
    handleDelete,
    isDeleting,
    translations,
    isLoadingData,
  } = useEditForm({
    id,
    client,
    table_name,
    FormSchema,
    fieldsNotApplyTranslation: [],
    adjustOnReset: (key, value, fetchedData) => {
      if (key === "c8_static_datum_name") {
        return {
          text: value,
          id: fetchedData.c8_static_datum_id,
          asset_class_2: fetchedData.c8_static_datum_asset_class_2,
          asset_class_1: fetchedData.c8_static_datum_asset_class_1,
          currency: fetchedData.c8_static_datum_currency,
        };
      }
      return value;
    },
  });

  const { category_options, isCategoryDisabled } = useCategoryAutoSelection({
    form,
    translations,
    keysAllowNullValue: [],
    isLoadingData,
  });
  const isCategoriesEmpty = category_options.options.length == 0;

  const asset = form.watch("c8_static_datum_name");
  const assetId = asset?.id;
  const assetCurrency = asset?.currency;
  const assetClass2 = asset?.asset_class_2 ?? null;

  const localCurrency = useMemo(() => {
    return assetCurrency || null;
  }, [assetCurrency]);

  const fixingMode: FixingMode | null = useMemo(
    () => computeFixingMode(assetClass2),
    [assetClass2],
  );

  const changesArmedRef = useRef(false);
  useEffect(() => {
    const sub = form.watch((_values, info) => {
      // arm on any meaningful change
      if (
        info?.name === "c8_static_datum_name" ||
        info?.name === "market_value_local_ccy"
      ) {
        changesArmedRef.current = true;
      }
    });
    return () => sub?.unsubscribe?.();
  }, [form]);

  const hadInitialQuantityRef = useRef(
    form.getValues("quantity") !== null &&
      form.getValues("quantity") !== undefined,
  );
  const isShowQuantity = fixingMode === FixingMode.FIXING;
  const showQuantityField = changesArmedRef.current
    ? isShowQuantity
    : hadInitialQuantityRef.current || isShowQuantity;

  return (
    <div className="grid place-items-center bg-gray-50 h-full w-full max-w-card">
      <div className="flex justify-center w-full">
        <Card className="p-4 bg-white w-full">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                {t("transactions.transaction")} {id}
              </CardHeader>
              <Divider />
              <CardBody className="flex flex-col gap-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <DatePickerField
                    name="date"
                    label={t("transactions.transaction_date")}
                    isRequired={true}
                    control={form.control}
                  />
                </div>
                <AutocompleteAsyncField
                  name="c8_static_datum_name"
                  label={t("transactions.asset")}
                  isRequired={true}
                  control={form.control}
                  base_url="c8/c8_static_data"
                  id_column="c8_static_datum_id"
                  column_name="name"
                  options={{
                    translateKey: "asset_class_2",
                    client: "c8",
                    table_name: "c8_static_data",
                    language: i18n.resolvedLanguage?.startsWith("fr")
                      ? "fr"
                      : "en",
                  }}
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    isRequired={true}
                    name="market_value_local_ccy"
                    label={t("transactions.amount_local")}
                    type="number"
                    control={form.control}
                    endContent={
                      <div className="opacity-50 text-xs">{localCurrency}</div>
                    }
                  />
                  <InputField
                    name="market_value_ptf_ccy"
                    label={t("transactions.amount_reporting")}
                    control={form.control}
                    type="number"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="quantity"
                    label={t("transactions.quantity")}
                    control={form.control}
                    type="number"
                    isRequired={showQuantityField}
                    fieldOptions={{
                      isDisabled: !showQuantityField,
                      changeOnDisable: false,
                    }}
                  />
                  <SelectFieldWithSection
                    allow_empty_option={true}
                    label={t("transactions.movement_type")}
                    name="category"
                    sections={category_options.options as TSections}
                    control={form.control}
                    fieldOptions={{
                      isDisabled: isCategoryDisabled,
                      isVisible: !isCategoryDisabled || !isCategoriesEmpty,
                    }}
                  />
                </div>
                <div className="grid grid-cols-1 gap-4">
                  <TextareaField
                    name="description"
                    label={t("transactions.movement_description")}
                    control={form.control}
                  />
                </div>
              </CardBody>
              <CardFooter>
                <FormActionsUpdate
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                  isDirty={form.formState.isDirty}
                  elementId={id}
                  client={client}
                  allowDelete={false}
                />
              </CardFooter>
            </form>
          </Form>
        </Card>
        <div className="absolute top-12 right-12 2xl:top-10 2xl:right-10 md:flex gap-2 flex-row hidden">
          {assetId && (
            <Button
              href={toWorkspacePath(`/admin/c8/c8_static_data/${assetId}`)}
              variant="bordered"
              color="primary"
              showAnchorIcon
              as={ExternalLink}
              radius="full"
              className="bg-white border-1 border-neutral-300 shadow-xl"
            >
              Actif
            </Button>
          )}
        </div>
        <ConfirmationModal
          isOpen={isConfirmationModalOpen}
          isLoading={isUpdating}
          onClose={cancelUpdate}
          onConfirm={confirmUpdate}
          title={t("confirm_update")}
          children={
            changedFields
              ? Diff(
                  changedFields,
                  ["c8_static_datum_id"],
                  fieldTranslations,
                  table_name,
                )
              : t("no_changes")
          }
        />

        <DeleteConfirmationModal
          isOpen={isDeleteModalOpen}
          onClose={closeDeleteModal}
          onDelete={handleDelete}
          isLoading={isDeleting}
        />
      </div>
    </div>
  );
}
